import React, { useState } from 'react';
import { Input, Modal, Spin, DatePicker } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAnnouncementForm } from './hooks/useAnnouncementForm';
import { SharedActionBar } from '../../components/SharedActionBar';
import dayjs from 'dayjs';

const { TextArea } = Input;

const T102Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const {
    mode, isEditing, loading, records, current, currentIdx, form,
    setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, doCopy, quickSearch,
  } = useAnnouncementForm();

  const [searchText, setSearchText] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);

  // Load review record if ?review= is present
  React.useEffect(() => {
    if (reviewId) {
      quickSearch(reviewId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);
  const [copyCode, setCopyCode] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除公告「${current.ft121_01}」（${current.ft121_21}）？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: doDelete,
    });
  };

  const handleCopy = () => { setCopyCode(''); setCopyModalOpen(true); };
  const handleCopyOk = () => {
    if (!copyCode.trim()) return;
    doCopy(copyCode.trim());
    setCopyModalOpen(false);
  };

  // 審核流已拆除（批次 5，2026-06-12 依使用者裁決移除——FoxPro 規格（pwt102f1）
  // 無 FT121_80 審核概念，公告建檔即發布生效，原送審/核准/退回/沖銷 UI 為重建版誤加）

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  const toDateValue = (v: string | null | undefined) => v ? dayjs(v) : null;
  const fromDateValue = (d: dayjs.Dayjs | null) =>
    d ? d.format('YYYY-MM-DD') + 'T00:00:00Z' : null;

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          公告建檔 (pwt102f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick search */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="序號/主題查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && quickSearch(searchText.trim())}
            disabled={isEditing} style={{ width: 200 }} />
          <button className="sab-btn" onClick={() => searchText.trim() && quickSearch(searchText.trim())}
            disabled={isEditing}>查詢</button>
        </div>

        {/* Form fields */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              公告序號 (FT121_01)
            </label>
            <Input value={form.ft121_01 || (mode === 'add' ? '*自動產生*' : '')}
              disabled style={{ width: 200 }} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              開始日期 (FT121_11) <span className="text-red-500">*</span>
            </label>
            <DatePicker value={toDateValue(form.ft121_11)}
              onChange={d => setField('ft121_11', fromDateValue(d))}
              disabled={!isEditing} style={{ width: 200 }} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              結束日期 (FT121_12) <span className="text-red-500">*</span>
            </label>
            <DatePicker value={toDateValue(form.ft121_12)}
              onChange={d => setField('ft121_12', fromDateValue(d))}
              disabled={!isEditing} style={{ width: 200 }} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              公告主題 (FT121_21) <span className="text-red-500">*</span>
            </label>
            <Input value={form.ft121_21}
              onChange={e => setField('ft121_21', e.target.value)}
              maxLength={60} disabled={!isEditing} style={{ width: 500 }}
              placeholder="最多60字元" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              公告內容 (FT121_22)
            </label>
            <TextArea value={form.ft121_22}
              onChange={e => setField('ft121_22', e.target.value)}
              disabled={!isEditing} rows={6} style={{ width: 500 }}
              placeholder="公告詳細內容" />
          </div>
        </div>

        {/* Metadata display */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6">
            建立人員: {current.ft121_91 || '-'} |
            建立日期: {current.ft121_92 ? dayjs(current.ft121_92).format('YYYY-MM-DD') : '-'} |
            修改人員: {current.ft121_93 || '-'}
          </div>
        )}

        {/* SharedActionBar */}
        <SharedActionBar
          mode={mode} isEditing={isEditing} hasRecord={!!current}
          currentIdx={currentIdx} recordCount={records.length}
          onNavFirst={navFirst} onNavPrev={navPrev}
          onNavNext={navNext} onNavLast={navLast}
          onAdd={startAdd} onModify={startEdit} onDelete={handleDelete}
          onSave={save} onCancel={cancel}
          onCopy={handleCopy}
          onExit={() => navigate(-1)}
        />

        {/* Copy modal */}
        <Modal title="複製公告" open={copyModalOpen} onOk={handleCopyOk}
          onCancel={() => setCopyModalOpen(false)} okText="確定" cancelText="取消">
          <div className="mb-2">來源序號：{current?.ft121_01}</div>
          <Input placeholder="請輸入新序號" value={copyCode}
            onChange={e => setCopyCode(e.target.value)}
            maxLength={10} style={{ width: 200 }} />
        </Modal>

      </div>
    </Spin>
  );
};

export default T102Form;
